import { isReauthKind } from '@felix/domain';
import { Alert, Button, Drawer, Group } from '@mantine/core';
import { IconArrowBackUp, IconCheck, IconInfoCircle } from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime, formatDay } from '../../format.js';
import classes from './Pms.module.css';
import work from './PmsWork.module.css';
import { PmsPasswordDialog } from './PmsWorkParts.js';
import { frequencyLabel, type IntervalLike, numberFormat } from './plan-view.js';
import type { DecisionInput, PendingItem } from './types.js';

export type DecisionKind = 'postponement' | 'interval' | 'record' | 'defect' | 'breakdown';

export function decisionKind(item: Pick<PendingItem, 'recordType'>): DecisionKind {
  if (item.recordType === 'postponement_request') return 'postponement';
  if (item.recordType === 'maintenance_interval_change') return 'interval';
  // Arıza ve breakdown kapanışı (K24)
  if (item.recordType === 'defect_report') return 'defect';
  if (item.recordType === 'breakdown_report') return 'breakdown';
  return 'record';
}

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);
const number = (value: unknown) => (typeof value === 'number' ? value : null);

/**
 * Karar verilen içerik (onaylayıcı hash'i alınan alanları görür; mimari belge §7): erteleme
 * talebinde güncel, istenen ve ilk orijinal vade [NR496 4.5.3d]; aralık değişikliğinde önceki ve
 * yeni aralık (K11).
 */
export function DecisionDetails({ item, extra }: { item: PendingItem; extra?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const k = (key: string) => t(`pms.work.decision.${key}`);
  const language = i18n.resolvedLanguage ?? 'en';
  const c = item.content;
  const hours = (value: unknown) => {
    const n = number(value);
    return n === null ? null : t('pms.hours', { value: numberFormat(language, n) });
  };
  const due = (date: unknown, h: unknown) =>
    [text(date) ? formatDay(String(date)) : null, hours(h)].filter(Boolean).join(' · ') || '—';
  const kind = decisionKind(item);
  const defect = kind === 'defect' || kind === 'breakdown';
  const rows: [string, string][] = [
    defect
      ? [t('pms.condition.defects.columns.no'), item.defectNo ?? '—']
      : [k('job'), [item.jobCode, item.jobTitle].filter(Boolean).join(' · ') || '—'],
    [k('component'), item.equipmentName ?? '—'],
  ];
  if (defect) {
    // Kapanış içeriği arıza kartındadır (extra): onaylayan hash'i alınan alanları görür (§7)
  } else if (kind === 'postponement') {
    rows.push(
      [k('currentDue'), due(c.previousDueOn, c.previousDueHours)],
      [k('newDue'), due(c.newDueOn, c.newDueHours)],
      [k('firstOriginalDue'), due(c.firstOriginalDueOn, c.firstOriginalDueHours)],
    );
  } else if (kind === 'interval') {
    const interval = (value: unknown) =>
      value && typeof value === 'object' ? frequencyLabel(value as IntervalLike, t, language) : '—';
    rows.push(
      [k('previousInterval'), interval(c.previousInterval)],
      [k('newInterval'), interval(c.newInterval)],
      [
        k('direction'),
        t(`pms.work.decision.directions.${String(c.direction)}`, { defaultValue: '—' }),
      ],
      [k('basis'), t(`pms.work.bases.${String(c.intervalBasis)}`, { defaultValue: '—' })],
    );
  } else {
    rows.push(
      [k('doneOn'), formatDay(text(c.doneOn))],
      [k('result'), t(`pms.work.results.${String(c.result)}`, { defaultValue: '—' })],
      [k('workPerformed'), text(c.workPerformed) ?? '—'],
    );
  }
  if (kind === 'postponement' || kind === 'interval') {
    rows.push(
      [k('reason'), text(c.reason) ?? '—'],
      [k('classReference'), text(c.classReference) ?? '—'],
    );
  }
  rows.push([k('since'), item.since ? formatDateTime(item.since) : '—']);
  return (
    <>
      <dl className={classes.detailRows}>
        {rows.map(([label, value]) => (
          <div key={label} className={classes.detailRow}>
            <dt>{label}</dt>
            <dd className={classes.notes}>{value}</dd>
          </div>
        ))}
      </dl>
      {extra}
    </>
  );
}

export interface DecisionDrawerProps {
  opened: boolean;
  item: PendingItem | null;
  /** `office`: süperintendent adımı (web); `ship`: kaptan adımı (gemi). */
  at: 'office' | 'ship';
  onClose: () => void;
  onDecide: (item: PendingItem, input: DecisionInput) => Promise<void>;
  /** Karar verilen kaydın ayrıntısı (arıza ve breakdown kartı; M7c). */
  details?: ReactNode;
}

/**
 * Karar çekmecesi (ADR 0026 §3; K10, K11): içerik, Approve ve Return; açık onay adımı (parola yalnız
 * şirket ayarı açıkken, ADR 0075), iade gerekçe ister (mimari belge §7). Karar ekranda görülen gönderim ve
 * adımla verilir (bayat karar yazılmaz).
 */
export function DecisionDrawer({
  opened,
  item,
  at,
  onClose,
  onDecide,
  details,
}: DecisionDrawerProps) {
  const { t } = useTranslation();
  const [action, setAction] = useState<'approved' | 'returned' | null>(null);
  if (!item) return null;
  const kind = decisionKind(item);
  const title = t(`pms.work.decision.title.${kind}`);
  const confirm = async ({ reason, password }: { reason: string; password?: string }) => {
    if (!action) return null;
    await onDecide(item, {
      contentHash: item.contentHash,
      submissionId: item.submissionId,
      step: item.step,
      decision: action,
      ...(reason ? { reason } : {}),
      ...(password ? { password } : {}),
    });
    setAction(null);
    return null;
  };
  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size={520}
        title={<span className={classes.drawerTitle}>{title}</span>}
        closeButtonProps={{ 'aria-label': t('pms.drawer.close') }}
      >
        <div className={work.drawerStack}>
          <Alert color="blue" icon={<IconInfoCircle size={16} />}>
            {t(`pms.work.decision.step.${at}`, { step: item.step })}
          </Alert>
          <DecisionDetails item={item} extra={details} />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              color="red"
              leftSection={<IconArrowBackUp size={16} />}
              onClick={() => setAction('returned')}
            >
              {t('pms.work.decision.return')}
            </Button>
            <Button leftSection={<IconCheck size={16} />} onClick={() => setAction('approved')}>
              {t('pms.work.decision.approve')}
            </Button>
          </Group>
        </div>
      </Drawer>
      <PmsPasswordDialog
        opened={action !== null}
        title={
          action === 'returned'
            ? t('pms.work.decision.returnTitle')
            : t('pms.work.decision.approveTitle')
        }
        body={t(
          action === 'returned' ? 'pms.work.decision.returnBody' : 'pms.work.decision.approveBody',
          {
            name: title,
          },
        )}
        reason={action === 'returned' ? 'required' : 'optional'}
        confirmLabel={
          action === 'returned' ? t('pms.work.decision.return') : t('pms.work.decision.approve')
        }
        danger={action === 'returned'}
        onClose={() => setAction(null)}
        {...(isReauthKind(item.recordType) ? { reauthKind: item.recordType } : {})}
        onConfirm={confirm}
      />
    </>
  );
}

/**
 * Ofis karar çekmecesi (web; plan M7 Task 14a, 21b): süperintendent ertelemenin, aralık uzatmasının
 * (K10, K11) ve breakdown kapanışının (K24) ofis adımını karara bağlar.
 */
export function OfficeDecisionDrawer(props: Omit<DecisionDrawerProps, 'at'>) {
  return <DecisionDrawer {...props} at="office" />;
}
